import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layers, Plus, ArrowLeftRight, DollarSign, Eye, Search, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const ShareAccountsPage = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showTransfer, setShowTransfer] = useState(null);
  const [showRedeem, setShowRedeem] = useState(null);
  const [showDividend, setShowDividend] = useState(false);
  const [showRegister, setShowRegister] = useState(null);
  const [registerData, setRegisterData] = useState(null);
  const [search, setSearch] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');

  const [createForm, setCreateForm] = useState({ client_id: '', product_id: '', shares_requested: '', investor_type: 'client' });
  const [transferForm, setTransferForm] = useState({ to_client_id: '', shares: '' });
  const [redeemForm, setRedeemForm] = useState({ shares: '' });
  const [dividendForm, setDividendForm] = useState({ product_id: '', dividend_per_share: '' });
  const [clientSearch, setClientSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, prodRes, cliRes] = await Promise.all([
        api.get('/share-accounts'),
        api.get('/share-products'),
        api.get('/clients?limit=500').catch(() => ({ data: [] })),
      ]);
      setAccounts(accRes.data || []);
      setProducts(prodRes.data || []);
      setClients(Array.isArray(cliRes.data) ? cliRes.data : cliRes.data?.clients || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = accounts.filter(a => {
    if (filterProduct !== 'all' && a.product_id !== filterProduct) return false;
    if (search) {
      const s = search.toLowerCase();
      return (a.client_name || '').toLowerCase().includes(s) || (a.account_number || '').toLowerCase().includes(s);
    }
    return true;
  });

  const handleCreate = async () => {
    try {
      await api.post('/share-accounts', createForm);
      toast.success('Share account created — pending approval');
      setShowCreate(false);
      setCreateForm({ client_id: '', product_id: '', shares_requested: '', investor_type: 'client' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/share-accounts/${id}/approve`);
      toast.success('Share account approved — shares issued');
      load();
      setShowDetail(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRedeem = async () => {
    if (!showRedeem) return;
    try {
      const res = await api.post(`/share-accounts/${showRedeem.id}/redeem`, { shares: parseInt(redeemForm.shares) });
      toast.success(res.data.message);
      setShowRedeem(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleTransfer = async () => {
    if (!showTransfer) return;
    try {
      const res = await api.post(`/share-accounts/${showTransfer.id}/transfer`, { to_client_id: transferForm.to_client_id, shares: parseInt(transferForm.shares) });
      toast.success(res.data.message);
      setShowTransfer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDividend = async () => {
    try {
      const res = await api.post('/share-accounts/distribute-dividends', { product_id: dividendForm.product_id, dividend_per_share: parseFloat(dividendForm.dividend_per_share) });
      toast.success(res.data.message);
      setShowDividend(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openRegister = async (productId) => {
    try {
      const res = await api.get(`/share-register/${productId}`);
      setRegisterData(res.data);
      setShowRegister(productId);
    } catch (e) { toast.error('Failed to load register'); }
  };

  const filteredClients = clientSearch
    ? clients.filter(c => `${c.first_name} ${c.last_name} ${c.client_number || ''}`.toLowerCase().includes(clientSearch.toLowerCase())).slice(0, 20)
    : clients.slice(0, 20);

  const statusColors = { pending_approval: 'bg-amber-50 text-amber-700', active: 'bg-green-50 text-green-700', closed: 'bg-gray-100 text-gray-500' };
  const canApprove = ['admin', 'manager'].includes(user?.role);
  const canProcess = ['admin', 'manager', 'teller', 'loan_officer'].includes(user?.role);

  return (
    <div className="space-y-6 fade-in" data-testid="share-accounts-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Clients</span><span>/</span><span className="text-[#1f1f1f] font-medium">Share Accounts</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">SHARE MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Share Accounts</h1>
        </div>
        <div className="flex gap-2">
          {canApprove && (
            <Button variant="outline" size="sm" onClick={() => setShowDividend(true)} className="text-xs gap-1.5" data-testid="dividend-btn">
              <DollarSign className="w-3.5 h-3.5" /> Distribute Dividends
            </Button>
          )}
          {canProcess && (
            <Button onClick={() => setShowCreate(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white text-sm gap-1.5" data-testid="create-share-btn">
              <Plus className="w-4 h-4" /> Subscribe to Shares
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search by client or account..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 text-sm" data-testid="share-search" />
        </div>
        <Select value={filterProduct} onValueChange={setFilterProduct}>
          <SelectTrigger className="w-[200px]" data-testid="share-filter-product"><SelectValue placeholder="All Products" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {products.length > 0 && (
          <Select value="" onValueChange={v => v && openRegister(v)}>
            <SelectTrigger className="w-[180px]" data-testid="share-register-select"><SelectValue placeholder="View Register..." /></SelectTrigger>
            <SelectContent>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Account</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Client</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Shares</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Value</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Dividends</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            filtered.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">No share accounts found.</td></tr> :
            filtered.map(a => (
              <tr key={a.id} className="border-b hover:bg-gray-50" data-testid={`share-row-${a.id}`}>
                <td className="px-4 py-3 text-sm font-mono text-[#1f1f1f]">{a.account_number}</td>
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-[#1f1f1f]">{a.client_name}</p>
                  <p className="text-[10px] text-gray-400">{a.client_number}</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{a.product_name}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 capitalize">{a.investor_type || 'client'}</span></td>
                <td className="px-4 py-3 text-sm text-right font-mono">{a.shares_approved || 0}</td>
                <td className="px-4 py-3 text-sm text-right font-medium">{fmt(a.total_value)}</td>
                <td className="px-4 py-3 text-sm text-right text-emerald-600">{fmt(a.dividends_earned)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${statusColors[a.status] || 'bg-gray-100 text-gray-500'}`}>{(a.status || '').replace(/_/g, ' ')}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setShowDetail(a)} data-testid={`share-view-${a.id}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    {a.status === 'pending_approval' && canApprove && (
                      <Button size="sm" className="h-6 px-2 text-xs bg-green-600 text-white" onClick={() => handleApprove(a.id)} data-testid={`share-approve-${a.id}`}>Approve</Button>
                    )}
                    {a.status === 'active' && canProcess && (
                      <>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => { setShowTransfer(a); setTransferForm({ to_client_id: '', shares: '' }); }} data-testid={`share-transfer-${a.id}`}>
                          <ArrowLeftRight className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-red-600 border-red-200" onClick={() => { setShowRedeem(a); setRedeemForm({ shares: '' }); }} data-testid={`share-redeem-${a.id}`}>Sell</Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Share Account Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" aria-describedby="share-create-desc">
          <DialogHeader><DialogTitle>Subscribe to Shares</DialogTitle></DialogHeader>
          <p id="share-create-desc" className="sr-only">Subscribe a client to a share product</p>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Input placeholder="Search client..." value={clientSearch} onChange={e => setClientSearch(e.target.value)} className="mb-1 text-sm" data-testid="share-client-search" />
              {clientSearch && (
                <div className="max-h-32 overflow-y-auto border border-gray-200 bg-white">
                  {filteredClients.map(c => (
                    <button key={c.id} onClick={() => { setCreateForm({...createForm, client_id: c.id}); setClientSearch(`${c.first_name} ${c.last_name}`); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${createForm.client_id === c.id ? 'bg-blue-50' : ''}`}>
                      {c.first_name} {c.last_name} <span className="text-gray-400">({c.client_number})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Share Product *</Label>
              <Select value={createForm.product_id} onValueChange={v => setCreateForm({...createForm, product_id: v})}>
                <SelectTrigger data-testid="share-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.is_active !== false).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — R{p.unit_price || p.nominal_price || 0}/share</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Number of Shares *</Label><Input type="number" min="1" value={createForm.shares_requested} onChange={e => setCreateForm({...createForm, shares_requested: e.target.value})} data-testid="share-qty" /></div>
              <div><Label>Investor Type</Label>
                <Select value={createForm.investor_type} onValueChange={v => setCreateForm({...createForm, investor_type: v})}>
                  <SelectTrigger data-testid="share-investor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client (Individual)</SelectItem>
                    <SelectItem value="dfi">DFI / Development Finance</SelectItem>
                    <SelectItem value="institution">Institution</SelectItem>
                    <SelectItem value="group">Group / Community</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {createForm.product_id && createForm.shares_requested && (
              <div className="p-3 bg-gray-50 border border-gray-200 text-xs">
                <p>Total Cost: <span className="font-bold text-[#1f1f1f]">{fmt(parseInt(createForm.shares_requested || 0) * (products.find(p => p.id === createForm.product_id)?.unit_price || products.find(p => p.id === createForm.product_id)?.nominal_price || 0))}</span></p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#1f1f1f] text-white" data-testid="share-create-submit">Subscribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="share-detail-desc">
          <DialogHeader><DialogTitle>Share Account: {showDetail?.account_number}</DialogTitle></DialogHeader>
          <p id="share-detail-desc" className="sr-only">Share account details</p>
          {showDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Client:</span> <span className="font-medium">{showDetail.client_name}</span></div>
                <div><span className="text-gray-500">Product:</span> <span className="font-medium">{showDetail.product_name}</span></div>
                <div><span className="text-gray-500">Shares Held:</span> <span className="font-mono font-bold">{showDetail.shares_approved}</span></div>
                <div><span className="text-gray-500">Value:</span> <span className="font-bold">{fmt(showDetail.total_value)}</span></div>
                <div><span className="text-gray-500">Dividends Earned:</span> <span className="text-emerald-600 font-bold">{fmt(showDetail.dividends_earned)}</span></div>
                <div><span className="text-gray-500">Lock-in Until:</span> <span>{showDetail.lock_in_until ? showDetail.lock_in_until.slice(0, 10) : 'None'}</span></div>
              </div>
              {showDetail.transactions?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Transaction History</h4>
                  <div className="border border-gray-200 max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b">
                        <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th>
                        <th className="text-right px-3 py-2">Shares</th><th className="text-right px-3 py-2">Amount</th>
                      </tr></thead>
                      <tbody>
                        {showDetail.transactions.map((t, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-3 py-1.5">{t.date?.slice(0, 10)}</td>
                            <td className="px-3 py-1.5 capitalize">{(t.type || '').replace(/_/g, ' ')}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{t.shares}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmt(t.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={!!showTransfer} onOpenChange={() => setShowTransfer(null)}>
        <DialogContent className="max-w-md" aria-describedby="share-transfer-desc">
          <DialogHeader><DialogTitle>Transfer Shares</DialogTitle></DialogHeader>
          <p id="share-transfer-desc" className="sr-only">Transfer shares to another client</p>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">From: <span className="font-medium text-[#1f1f1f]">{showTransfer?.client_name}</span> ({showTransfer?.shares_approved} shares)</p>
            <div>
              <Label>To Client *</Label>
              <Select value={transferForm.to_client_id} onValueChange={v => setTransferForm({...transferForm, to_client_id: v})}>
                <SelectTrigger data-testid="transfer-to-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.id !== showTransfer?.client_id).slice(0, 50).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.client_number})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Shares to Transfer *</Label><Input type="number" min="1" max={showTransfer?.shares_approved} value={transferForm.shares} onChange={e => setTransferForm({...transferForm, shares: e.target.value})} data-testid="transfer-shares" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(null)}>Cancel</Button>
            <Button onClick={handleTransfer} className="bg-[#1f1f1f] text-white" data-testid="transfer-submit">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem Dialog */}
      <Dialog open={!!showRedeem} onOpenChange={() => setShowRedeem(null)}>
        <DialogContent className="max-w-sm" aria-describedby="share-redeem-desc">
          <DialogHeader><DialogTitle>Sell / Redeem Shares</DialogTitle></DialogHeader>
          <p id="share-redeem-desc" className="sr-only">Sell shares back</p>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">{showRedeem?.client_name} holds {showRedeem?.shares_approved} shares @ {fmt(showRedeem?.unit_price)}/share</p>
            <div><Label>Shares to Sell *</Label><Input type="number" min="1" max={showRedeem?.shares_approved} value={redeemForm.shares} onChange={e => setRedeemForm({...redeemForm, shares: e.target.value})} data-testid="redeem-shares" /></div>
            {redeemForm.shares > 0 && <p className="text-sm font-bold">Payout: {fmt(parseInt(redeemForm.shares || 0) * (showRedeem?.unit_price || 0))}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeem(null)}>Cancel</Button>
            <Button onClick={handleRedeem} className="bg-red-600 text-white" data-testid="redeem-submit">Sell Shares</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dividend Dialog */}
      <Dialog open={showDividend} onOpenChange={setShowDividend}>
        <DialogContent className="max-w-sm" aria-describedby="share-dividend-desc">
          <DialogHeader><DialogTitle>Distribute Dividends</DialogTitle></DialogHeader>
          <p id="share-dividend-desc" className="sr-only">Distribute dividends to all shareholders</p>
          <div className="space-y-4">
            <div><Label>Share Product *</Label>
              <Select value={dividendForm.product_id} onValueChange={v => setDividendForm({...dividendForm, product_id: v})}>
                <SelectTrigger data-testid="dividend-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Dividend per Share (ZAR) *</Label><Input type="number" step="0.01" min="0.01" value={dividendForm.dividend_per_share} onChange={e => setDividendForm({...dividendForm, dividend_per_share: e.target.value})} data-testid="dividend-amount" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDividend(false)}>Cancel</Button>
            <Button onClick={handleDividend} className="bg-[#1f1f1f] text-white" data-testid="dividend-submit">Distribute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Register Dialog */}
      <Dialog open={!!showRegister} onOpenChange={() => setShowRegister(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" aria-describedby="share-register-desc">
          <DialogHeader><DialogTitle>Share Register: {registerData?.product_name}</DialogTitle></DialogHeader>
          <p id="share-register-desc" className="sr-only">Shareholder register</p>
          {registerData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-gray-50 border"><p className="text-[10px] text-gray-500 uppercase">Total Shares</p><p className="font-bold">{registerData.total_shares}</p></div>
                <div className="p-3 bg-emerald-50 border border-emerald-200"><p className="text-[10px] text-gray-500 uppercase">Issued</p><p className="font-bold text-emerald-700">{registerData.shares_issued}</p></div>
                <div className="p-3 bg-blue-50 border border-blue-200"><p className="text-[10px] text-gray-500 uppercase">Available</p><p className="font-bold text-blue-700">{registerData.shares_available}</p></div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Shareholder</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Type</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Shares</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Value</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">Dividends</th>
                </tr></thead>
                <tbody>
                  {registerData.holders?.map((h, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-2 font-medium">{h.client_name}</td>
                      <td className="px-3 py-2 capitalize text-xs">{h.investor_type}</td>
                      <td className="px-3 py-2 text-right font-mono">{h.shares}</td>
                      <td className="px-3 py-2 text-right">{fmt(h.total_value)}</td>
                      <td className="px-3 py-2 text-right text-emerald-600">{fmt(h.dividends_earned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShareAccountsPage;
